"use client";

import { useTranslation } from "react-i18next";
import { Title, Text } from "@/components/ui/Headings";
import { Heart, Coffee, QrCode, ExternalLink } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import { useState } from "react";

interface SupportContentProps {
  bankDetails: {
    recipient: string;
    iban: string;
    bic: string;
    reference: string;
  };
  koFiUrl: string;
}

export function SupportContent({ bankDetails, koFiUrl }: SupportContentProps) {
  const { t } = useTranslation();
  const [showQRCode, setShowQRCode] = useState(false);

  // Generate SEPA QR code data (EPC QR Code format)
  // Format: BCD\n002\n1\nSCT\nBIC\nName\nIBAN\nEUR\nAmount\nReference\n\n\n
  const generateSEPAQRData = () => {
    if (!bankDetails.iban) {
      return "Please configure bank details";
    }
    const qrData = [
      "BCD",
      "002",
      "1",
      "SCT",
      bankDetails.bic || "",
      bankDetails.recipient,
      bankDetails.iban,
      "EUR",
      "", // Amount - leave empty for user to enter
      bankDetails.reference,
      "", // Purpose
      "", // Structured remittance
      "", // Unstructured remittance
    ].join("\n");
    return qrData;
  };

  const qrData = generateSEPAQRData();

  return (
    <>
      {/* Hero Section */}
      <section className="py-12 sm:py-16 bg-gradient-to-br from-primary-50 to-neutral-cream">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="flex justify-center mb-4">
            <Heart className="h-12 w-12 sm:h-16 sm:w-16 text-primary-600" />
          </div>
          <Title size="lg" className="mb-4">
            {t("support.hero.title")}
          </Title>
          <Text variant="subtitle" className="text-gray-900 leading-relaxed mb-6">
            {t("support.hero.subtitle")}
          </Text>
          <Text className="text-gray-700 max-w-2xl mx-auto">
            {t("support.hero.description")}
          </Text>
        </div>
      </section>

      {/* Support Options */}
      <section className="py-12 sm:py-16">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 lg:gap-12">
            {/* Ko-fi Support */}
            <div className="bg-white rounded-xl border border-gray-200 p-6 sm:p-8 shadow-sm">
              <div className="flex items-center space-x-3 mb-4">
                <Coffee className="h-6 w-6 text-primary-600" />
                <Title size="sm" className="font-semibold">
                  {t("support.koFi.title")}
                </Title>
              </div>
              <Text className="text-gray-700 mb-6">
                {t("support.koFi.description")}
              </Text>
              <a
                href={koFiUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center space-x-2 bg-[#FF5E5B] text-white px-6 py-3 rounded-lg font-semibold hover:bg-[#FF4A47] transition-colors w-full sm:w-auto justify-center"
              >
                <span>{t("support.koFi.button")}</span>
                <ExternalLink className="h-4 w-4" />
              </a>
            </div>

            {/* Bank Transfer */}
            <div className="bg-white rounded-xl border border-gray-200 p-6 sm:p-8 shadow-sm">
              <div className="flex items-center space-x-3 mb-4">
                <QrCode className="h-6 w-6 text-primary-600" />
                <Title size="sm" className="font-semibold">
                  {t("support.bankTransfer.title")}
                </Title>
              </div>
              <Text className="text-gray-700 mb-6">
                {t("support.bankTransfer.description")}
              </Text>

              {bankDetails.iban ? (
                <div className="space-y-4">
                  <div className="bg-primary-50 rounded-lg p-4 space-y-2">
                    <div>
                      <Text className="text-xs font-medium text-gray-600 mb-1">
                        {t("support.bankTransfer.recipient")}
                      </Text>
                      <Text className="text-gray-900">{bankDetails.recipient}</Text>
                    </div>
                    <div>
                      <Text className="text-xs font-medium text-gray-600 mb-1">
                        IBAN
                      </Text>
                      <Text className="text-gray-900 font-mono text-sm">
                        {bankDetails.iban}
                      </Text>
                    </div>
                    {bankDetails.bic && (
                      <div>
                        <Text className="text-xs font-medium text-gray-600 mb-1">
                          BIC
                        </Text>
                        <Text className="text-gray-900 font-mono text-sm">
                          {bankDetails.bic}
                        </Text>
                      </div>
                    )}
                    <div>
                      <Text className="text-xs font-medium text-gray-600 mb-1">
                        {t("support.bankTransfer.reference")}
                      </Text>
                      <Text className="text-gray-900 text-sm">
                        {bankDetails.reference}
                      </Text>
                    </div>
                  </div>

                  <button
                    onClick={() => setShowQRCode(!showQRCode)}
                    className="w-full bg-primary-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-primary-550 transition-colors"
                  >
                    {showQRCode
                      ? t("support.bankTransfer.hideQR")
                      : t("support.bankTransfer.showQR")}
                  </button>

                  {showQRCode && (
                    <div className="flex flex-col items-center space-y-4 pt-4 border-t border-gray-200">
                      <div className="bg-white p-4 rounded-lg border border-gray-300">
                        <QRCodeSVG
                          value={qrData}
                          size={200}
                          level="M"
                          includeMargin={true}
                        />
                      </div>
                      <Text className="text-xs text-gray-600 text-center max-w-xs">
                        {t("support.bankTransfer.qrInstructions")}
                      </Text>
                    </div>
                  )}
                </div>
              ) : (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                  <Text className="text-sm text-yellow-800">
                    {t("support.bankTransfer.configNeeded")}
                  </Text>
                </div>
              )}
            </div>
          </div>

          {/* Thank You Message */}
          <div className="mt-12 text-center">
            <div className="bg-primary-50 rounded-xl border border-primary-200 p-6 sm:p-8 max-w-3xl mx-auto">
              <Heart className="h-8 w-8 text-primary-600 mx-auto mb-4" />
              <Title size="sm" className="mb-3">
                {t("support.thankYou.title")}
              </Title>
              <Text className="text-gray-700">
                {t("support.thankYou.message")}
              </Text>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}

